import {
  AssetType,
  Placement,
  TransformationVariant,
} from '@amzn/fire-tv-campaign-automation-service';
import { useEffect, useRef } from 'react';
import { AssetTransformationHandler } from 'src/handlers/AssetTransformationHandler';
import { AssetState, AssetStatus } from 'src/types';

interface UseOverlayTransformParams {
  assetType: AssetType;
  status: AssetStatus;
  assetState: AssetState;
  setState: (update: Partial<AssetState>) => void;
  assetTransformationHandler: AssetTransformationHandler;
  placement: Placement;
  userAlias: string;
}

/**
 * Manages overlay transform lifecycle: tracks base image key, debounces overlay
 * transform requests, caches results, and reverts to base on failure or toggle-off.
 */
export const useOverlayTransform = ({
  assetType,
  status,
  assetState,
  setState,
  assetTransformationHandler,
  placement,
  userAlias,
}: UseOverlayTransformParams): void => {
  const overlayDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  const hasCompletedOnceRef = useRef(false);
  const baseTransformedKeyRef = useRef<string | undefined>();
  const pendingOverlayCountRef = useRef(0);
  const lastOverlayParamsRef = useRef<string | undefined>();
  const overlayCacheRef = useRef<Map<string, string>>(new Map());
  const CACHE_MAX_SIZE = 20;

  // Track completion status and cache overlay keys
  useEffect(() => {
    if (assetType !== AssetType.BACKGROUND_IMAGE) return;
    if (status === AssetStatus.COMPLETED) {
      hasCompletedOnceRef.current = true;
      if (pendingOverlayCountRef.current > 0) {
        pendingOverlayCountRef.current -= 1;
        if (lastOverlayParamsRef.current) {
          overlayCacheRef.current.set(lastOverlayParamsRef.current, assetState.key!);
          if (overlayCacheRef.current.size > CACHE_MAX_SIZE) {
            const oldest = overlayCacheRef.current.keys().next().value!;
            overlayCacheRef.current.delete(oldest);
          }
        }
      } else if (assetState.key !== baseTransformedKeyRef.current) {
        baseTransformedKeyRef.current = assetState.key;
        lastOverlayParamsRef.current = undefined;
        overlayCacheRef.current.clear();
      }
    } else if (status === AssetStatus.FAILED && pendingOverlayCountRef.current > 0) {
      pendingOverlayCountRef.current -= 1;
      lastOverlayParamsRef.current = undefined;
      if (baseTransformedKeyRef.current) {
        setState({ key: baseTransformedKeyRef.current, status: AssetStatus.COMPLETED });
      }
    }
  }, [assetType, status, assetState.key]);

  // Debounce overlay transform calls
  useEffect(() => {
    if (assetType !== AssetType.BACKGROUND_IMAGE) return;
    if (!hasCompletedOnceRef.current) return;
    if (status !== AssetStatus.COMPLETED) return;
    if (!baseTransformedKeyRef.current) return;

    const toggles = assetState.overlayToggles || {};
    const hasAnyOverlay =
      (toggles.contentBadge && assetState.contentBadge) ||
      (toggles.disclaimer && assetState.disclaimer) ||
      (toggles.contentRating && assetState.contentRating);

    if (!hasAnyOverlay) {
      if (lastOverlayParamsRef.current) {
        lastOverlayParamsRef.current = undefined;
        setState({ key: baseTransformedKeyRef.current });
      }
      return;
    }

    const activeToggles = Object.fromEntries(Object.entries(toggles).filter(([, v]) => v));
    const overlayParams = JSON.stringify({
      toggles: activeToggles,
      cb: toggles.contentBadge ? assetState.contentBadge : undefined,
      d: toggles.disclaimer ? assetState.disclaimer : undefined,
      cr: toggles.contentRating ? assetState.contentRating : undefined,
    });
    if (overlayParams === lastOverlayParamsRef.current) return;

    clearTimeout(overlayDebounceRef.current);
    overlayDebounceRef.current = setTimeout(() => {
      const cached = overlayCacheRef.current.get(overlayParams);
      if (cached) {
        lastOverlayParamsRef.current = overlayParams;
        pendingOverlayCountRef.current += 1;
        setState({ key: cached });
        return;
      }
      lastOverlayParamsRef.current = overlayParams;
      pendingOverlayCountRef.current += 1;
      assetTransformationHandler.handleTransformation({
        assetType,
        placement,
        actor: userAlias,
        sourceAssetKey: baseTransformedKeyRef.current,
        transformationVariant: TransformationVariant.OVERLAY_ONLY,
        contentBadgeText: toggles.contentBadge ? assetState.contentBadge : undefined,
        disclaimerText: toggles.disclaimer ? assetState.disclaimer : undefined,
        contentRating: toggles.contentRating ? assetState.contentRating : undefined,
      });
    }, 600);

    return () => clearTimeout(overlayDebounceRef.current);
  }, [
    assetType,
    status,
    assetState.overlayToggles,
    assetState.contentBadge,
    assetState.disclaimer,
    assetState.contentRating,
    assetTransformationHandler,
    placement,
    userAlias,
  ]);
};
